/**
 * Canvas Constraints Utility
 * 
 * Centralized collision detection, boundary clamping, and size constraint
 * logic for the drag-and-drop PDF canvas editor.
 */

// ─── Canvas Dimensions (PDF A4 at scale 1) ──────────────────────────────────
export const CANVAS_WIDTH = 595;
export const CANVAS_HEIGHT = 840;

// Signature tool labels ("Signed On" / "Signed By") extend 18px above and below
// the resizable area via CSS absolute positioning (top: -18px, bottom: -18px).
// Collision detection must include this overflow.
export const SIGNATURE_LABEL_OFFSET = 18;

// Picklist error label ("Add at least one option") extends 22px below
// the resizable area via CSS absolute positioning (bottom: -22px).
export const PICKLIST_ERROR_OFFSET = 22;

/**
 * Get the vertical overflow (top/bottom) for a tool type.
 * These are CSS-positioned child elements that extend beyond the Rnd box.
 * Boundary clamping must reserve space for them.
 *
 * @param {string} toolType
 * @param {{ hasError?: boolean }} options
 * @returns {{ top: number, bottom: number }}
 */
export function getToolOverflow(toolType, options = {}) {
  if (toolType === "Signature") {
    return { top: SIGNATURE_LABEL_OFFSET, bottom: SIGNATURE_LABEL_OFFSET };
  }
  if (toolType === "PickList" && options.hasError) {
    return { top: 0, bottom: PICKLIST_ERROR_OFFSET };
  }
  return { top: 0, bottom: 0 };
}

// ─── Per-tool size constraints (fallback defaults) ──────────────────────────
// These are used when supportedFields data is not available.
// The actual values come from the server via supportedFields in Redux.
export const TOOL_SIZE_CONSTRAINTS = {
  Signature: { minWidth: 120, minHeight: 50, maxWidth: 300, maxHeight: 130 },
  Text: { minWidth: 80, minHeight: 30, maxWidth: 400, maxHeight: 150 },
  Date: { minWidth: 110, minHeight: 32, maxWidth: 220, maxHeight: 60 },
  Checkbox: { minWidth: 24, minHeight: 24, maxWidth: 28, maxHeight: 28 },
  Email: { minWidth: 140, minHeight: 32, maxWidth: 400, maxHeight: 80 },
  PickList: { minWidth: 140, minHeight: 32, maxWidth: 350, maxHeight: 80 },
  Initials: { minWidth: 29, minHeight: 29, maxWidth: 60, maxHeight: 29 },
};

/**
 * Check if two rectangles overlap (AABB collision detection).
 * Uses a 2px tolerance to prevent false positives from adjacent tools.
 *
 * @param {{ x: number, y: number, width: number, height: number }} rect1
 * @param {{ x: number, y: number, width: number, height: number }} rect2
 * @returns {boolean}
 */
export function isOverlapping(rect1, rect2) {
  return (
    rect1.x + 2 < rect2.x + rect2.width &&
    rect1.x + rect1.width - 2 > rect2.x &&
    rect1.y + 2 < rect2.y + rect2.height &&
    rect1.y + rect1.height - 2 > rect2.y
  );
}

/**
 * Get the effective bounding box for a coordinate, accounting for
 * signature tool label overflow ("Signed On" top / "Signed By" bottom).
 * Non-signature tools return their bounds unchanged.
 *
 * @param {{ x: number, y: number, width: number, height: number, _isSignature?: boolean }} coord
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function getEffectiveBounds(coord) {
  if (coord._isSignature) {
    return {
      x: coord.x,
      y: coord.y - SIGNATURE_LABEL_OFFSET,
      width: coord.width,
      height: coord.height + SIGNATURE_LABEL_OFFSET * 2,
    };
  }
  return { x: coord.x, y: coord.y, width: coord.width, height: coord.height };
}

/**
 * Clamp a rectangle to stay within canvas bounds.
 * Ensures x, y are >= 0 and the tool doesn't overflow the right/bottom edges.
 * The optional `overflow` parameter reserves space for CSS-positioned child
 * elements (labels, error messages) that extend beyond the Rnd box.
 *
 * @param {{ x: number, y: number, width: number, height: number }} rect
 * @param {{ width?: number, height?: number }} canvas
 * @param {{ top?: number, bottom?: number }} overflow - extra space needed above/below
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function clampToCanvas(rect, canvas = {}, overflow = {}) {
  const cw = canvas.width || CANVAS_WIDTH;
  const ch = canvas.height || CANVAS_HEIGHT;
  const overflowTop = overflow.top || 0;
  const overflowBottom = overflow.bottom || 0;

  let { x, y, width, height } = rect;

  // Clamp size to canvas (accounting for overflow)
  width = Math.min(width, cw);
  height = Math.min(height, ch - overflowTop - overflowBottom);

  // Clamp position (reserve space for overflow elements)
  x = Math.max(0, Math.min(x, cw - width));
  y = Math.max(overflowTop, Math.min(y, ch - height - overflowBottom));

  return { x, y, width, height };
}

/**
 * Get size constraints for a given tool type.
 *
 * @param {string} toolType
 * @returns {{ minWidth: number, minHeight: number, maxWidth: number, maxHeight: number }}
 */
export function getToolConstraints(toolType) {
  return TOOL_SIZE_CONSTRAINTS[toolType] || TOOL_SIZE_CONSTRAINTS.Text;
}

/**
 * Clamp width and height to the min/max constraints for a tool type.
 * This MUST be called before every size state update.
 *
 * @param {number} width
 * @param {number} height
 * @param {{ minWidth: number, minHeight: number, maxWidth: number, maxHeight: number }} constraints
 * @returns {{ width: number, height: number }}
 */
export function clampSize(width, height, constraints) {
  return {
    width: Math.max(constraints.minWidth, Math.min(width, constraints.maxWidth)),
    height: Math.max(constraints.minHeight, Math.min(height, constraints.maxHeight)),
  };
}

/**
 * Validate a proposed position/size against all other coordinates on the same page.
 * Returns true if the proposed rect does NOT overlap any existing tool.
 *
 * @param {{ x: number, y: number, width: number, height: number }} newRect
 * @param {Array} allCoordinates - All tool coordinates on the current page
 * @param {string} excludeId - eleId of the tool being moved (exclude from check)
 * @returns {boolean} true if position is valid (no overlap)
 */
export function validateMove(newRect, allCoordinates, excludeId) {
  return !allCoordinates.some((coord) => {
    if (coord.eleId === excludeId) return false;
    // Use expanded bounds for signature tools (includes label overflow)
    return isOverlapping(newRect, getEffectiveBounds(coord));
  });
}

/**
 * Validate a proposed resize against canvas bounds, size constraints, and overlap.
 *
 * @param {{ x: number, y: number, width: number, height: number }} newRect
 * @param {Array} allCoordinates
 * @param {string} excludeId
 * @param {string} toolType
 * @returns {{ valid: boolean, clamped: { x: number, y: number, width: number, height: number } }}
 */
export function validateResize(newRect, allCoordinates, excludeId, toolType, canvas = {}) {
  const constraints = getToolConstraints(toolType);

  // Apply min and max size constraints
  let { x, y, width, height } = newRect;
  width = Math.max(constraints.minWidth, Math.min(width, constraints.maxWidth));
  height = Math.max(constraints.minHeight, Math.min(height, constraints.maxHeight));

  // Clamp to canvas
  const clamped = clampToCanvas({ x, y, width, height }, canvas);

  // Check overlap
  const noOverlap = validateMove(clamped, allCoordinates, excludeId);

  return { valid: noOverlap, clamped };
}
